/**
 * "What could go wrong" — the risks of one strategy, on its own surface.
 *
 * Opened from the card's button (owner, 2 Oct 2026). It reads the single risk list in
 * src/strategy/strategyRisks.ts, the same list the Statement of Advice prints, so the screen and the
 * document cannot disagree. Nothing is recorded about it being opened.
 *
 * THE LAYOUT IS THE POINT. The owner's brief: if it looks like a wall of bland text the client won't
 * focus. So: what is about THIS client comes first, in amber, because it is the part they cannot get
 * anywhere else; then one block per part of the plan, each point a bold claim with its own icon and
 * one sentence of why. Short lines, generous space, no paragraphs.
 */
import React from 'react';
import {
    Lock, Receipt, Home, Landmark, TrendingDown, Clock, Heart, Percent, Users, FileSignature,
    Repeat, Coins, ShieldCheck, CalendarDays, Wrench, AlertTriangle, type LucideIcon,
} from 'lucide-react';
import { Button } from '../ui/index.tsx';
import DialogShell, { DialogSection } from '../ui/DialogShell.tsx';
import type { RiskIcon, RiskPoint, StrategyRisks } from '../../strategy/strategyRisks.ts';
import { inReaderWords, isAdviserViewing, type HouseholdVoice } from '../../utils/householdWords.ts';

const ICONS: Record<RiskIcon, LucideIcon> = {
    lock: Lock, receipt: Receipt, home: Home, landmark: Landmark, trendDown: TrendingDown, clock: Clock,
    heart: Heart, percent: Percent, users: Users, fileSignature: FileSignature, repeat: Repeat, coins: Coins,
    shield: ShieldCheck, calendar: CalendarDays, wrench: Wrench, alert: AlertTriangle,
};

const Point = ({ point, tone, voice }: { point: RiskPoint; tone: 'amber' | 'slate'; voice: HouseholdVoice }) => {
    const Icon = ICONS[point.icon];
    return (
        <li className="flex gap-3.5">
            <span className={`mt-[3px] shrink-0 ${tone === 'amber' ? 'text-amber-600' : 'text-slate-400'}`}>
                <Icon size={17} strokeWidth={1.75} aria-hidden />
            </span>
            <p className="text-[14.5px] leading-relaxed text-slate-700">
                <span className="font-semibold text-slate-900">{inReaderWords(point.lead, voice)}</span> {inReaderWords(point.body, voice)}
            </p>
        </li>
    );
};

export interface StrategyRisksPanelProps {
    strategyName: string;
    risks: StrategyRisks;
    onClose: () => void;
    /** "your" for a client reading about themselves; "John and Kerry's" for an adviser's view. Superseded by `voice`. */
    possessive?: string;
    /** The reader's voice (householdVoice): "you" for the client, the names and "they" for an adviser. */
    voice?: HouseholdVoice;
}

/** A voice from the older `possessive` prop, for callers not yet passing one. */
function voiceOf(possessive: string): HouseholdVoice {
    if (possessive === 'your' || !isAdviserViewing()) return { you: true, names: 'you', Names: 'You', possessive: 'your', Possessive: 'Your', they: 'you', They: 'You', them: 'you', their: 'your', Their: 'Your', are: 'are', have: 'have', s: '', couple: false };
    const names = possessive.replace(/'s?$/, '');
    const plural = names.includes(' and ');
    return { you: false, names, Names: names, possessive, Possessive: possessive, they: 'they', They: 'They', them: 'them', their: 'their', Their: 'Their', are: plural ? 'are' : 'is', have: plural ? 'have' : 'has', s: plural ? '' : 's', couple: plural };
}

/** The risks, on the shared dialog chrome: amber accent, the client's own points first. */
const StrategyRisksPanel = ({ strategyName, risks, onClose, possessive = 'your', voice: given }: StrategyRisksPanelProps) => {
    const voice = given ?? voiceOf(possessive);
    const total = risks.forYou.length + risks.sections.reduce((n, s) => n + s.points.length, 0);
    const you = voice.names;
    return (
        <DialogShell
            titleId="strategy-risks-heading"
            testId="strategy-risks"
            eyebrow={strategyName}
            title="What could go wrong"
            intro={total > 0 ? <>Every plan has trade-offs. These are the {total} that matter for this one, so {you} can choose with {voice.their} eyes open. The same points are set out in the advice document.</> : <>Nothing in this plan stands out as a particular risk. The general points are set out in the advice document.</>}
            onClose={onClose}
            footer={
                <>
                    <p className="mr-auto text-xs leading-relaxed text-slate-500">None of this means the plan is wrong. It means {you} should be comfortable with each point before choosing it.</p>
                    <Button onClick={onClose} variant="primary" size="sm" className="shrink-0">Got it</Button>
                </>
            }
        >
            {risks.forYou.length > 0 && (
                <section className="mb-7 border-l-[3px] border-amber-500 pl-5">
                    <h3 className="mb-3 text-[12.5px] font-semibold text-amber-800">For {you} specifically</h3>
                    <ul className="space-y-3.5">
                        {risks.forYou.map((p) => (
                            <Point key={p.lead} point={p} tone="amber" voice={voice} />
                        ))}
                    </ul>
                </section>
            )}
            <div className="space-y-7">
                {risks.sections.map((section) => (
                    <section key={section.kind}>
                        <DialogSection className="mb-3.5">{inReaderWords(section.title, voice)}</DialogSection>
                        <ul className="space-y-3.5">
                            {section.points.map((p) => (
                                <Point key={p.lead} point={p} tone="slate" voice={voice} />
                            ))}
                        </ul>
                    </section>
                ))}
            </div>
        </DialogShell>
    );
};

export default StrategyRisksPanel;
